// ─── TOOL ICONS — Glassmorphism SVG Icon System ───────────────────────────────
// Dark glass card background + coral (#F05A41) accents
// All icons use a 48×48 viewBox and accept a `size` prop

const C = "#F05A41";   // coral
const G = "#D8A056";   // gold
const CARD = "#0F141C"; // glass card bg

// ─── GLASS PANEL (shared visual base) ────────────────────────────────────────
function Panel({ glowX = 30, glowY = 32, glowColor = C }) {
  return (
    <>
      {/* Dark card */}
      <rect x="1" y="1" width="46" height="46" rx="12"
        fill={CARD} stroke="rgba(255,255,255,0.09)" strokeWidth="0.75"/>
      {/* Top glass shine */}
      <rect x="1.5" y="1.5" width="45" height="20" rx="11.5"
        fill="rgba(255,255,255,0.055)"/>
      {/* Soft glow behind icon element */}
      <circle cx={glowX} cy={glowY} r="15" fill={glowColor} opacity="0.11"/>
    </>
  );
}

// ─── 1. JSON TOOLKIT ─────────────────────────────────────────────────────────
export function JsonIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={24} glowY={26}/>
      {/* Left brace */}
      <path d="M19 11 C15.5 11 14 13 14 16 L14 20.5 C14 22.5 12 24 12 24 C12 24 14 25.5 14 27.5 L14 32 C14 35 15.5 37 19 37"
        stroke={C} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Right brace */}
      <path d="M29 11 C32.5 11 34 13 34 16 L34 20.5 C34 22.5 36 24 36 24 C36 24 34 25.5 34 27.5 L34 32 C34 35 32.5 37 29 37"
        stroke={G} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Colon dots */}
      <circle cx="24" cy="20" r="1.8" fill={C}/>
      <circle cx="24" cy="28" r="1.8" fill={G}/>
    </svg>
  );
}

// ─── 2. ENCODING TOOLKIT (padlock) ───────────────────────────────────────────
export function EncodeIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={28} glowY={33}/>
      {/* Shackle */}
      <path d="M16 22 L16 16 Q16 9 24 9 Q32 9 32 16 L32 22"
        stroke={G} strokeWidth="2.6" strokeLinecap="round" fill="none"/>
      {/* Lock body */}
      <rect x="11" y="22" width="26" height="19" rx="5" fill={C} opacity="0.90"/>
      {/* Inner body highlight */}
      <rect x="11" y="22" width="26" height="8" rx="5" fill="rgba(255,255,255,0.08)"/>
      {/* Keyhole circle */}
      <circle cx="24" cy="31" r="4" fill={CARD} opacity="0.70"/>
      {/* Keyhole stem */}
      <rect x="22.5" y="33" width="3" height="5" rx="1.5" fill={CARD} opacity="0.70"/>
    </svg>
  );
}

// ─── 3. GENERATORS (lightning + spark) ───────────────────────────────────────
export function GeneratorsIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={26} glowY={28}/>
      {/* Lightning bolt */}
      <path d="M28 9 L15 27 L22.5 27 L20 39 L33 21 L25.5 21 Z"
        fill={C}/>
      {/* Highlight edge */}
      <path d="M28 9 L15 27 L22.5 27 L20 39 L33 21 L25.5 21 Z"
        stroke="rgba(255,255,255,0.18)" strokeWidth="0.75" fill="none"/>
      {/* Small spark dots */}
      <circle cx="36" cy="13" r="2" fill={G} opacity="0.85"/>
      <circle cx="39" cy="20" r="1.3" fill={G} opacity="0.55"/>
      <circle cx="11" cy="35" r="1.5" fill={G} opacity="0.55"/>
    </svg>
  );
}

// ─── 4. FAKE DATA GENERATOR (person + rows) ──────────────────────────────────
export function FakeDataIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={26} glowY={24}/>
      {/* Person head */}
      <circle cx="19" cy="16" r="5.5" fill={C}/>
      {/* Shoulders */}
      <path d="M9 35 Q9 25 19 25 Q29 25 29 35" fill={C} opacity="0.85"/>
      {/* Data lines — right side */}
      <rect x="31" y="13" width="11" height="2.5" rx="1.25" fill={G} opacity="0.90"/>
      <rect x="31" y="19" width="8"  height="2.5" rx="1.25" fill={G} opacity="0.70"/>
      <rect x="31" y="25" width="10" height="2.5" rx="1.25" fill={G} opacity="0.80"/>
      <rect x="31" y="31" width="6"  height="2.5" rx="1.25" fill={G} opacity="0.55"/>
      <rect x="31" y="37" width="9"  height="2.5" rx="1.25" fill={G} opacity="0.65"/>
      {/* Divider line */}
      <line x1="29" y1="11" x2="29" y2="41" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
    </svg>
  );
}

// ─── 5. HASH & SECURITY (# symbol + shield accent) ───────────────────────────
export function HashIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={24} glowY={27}/>
      {/* Vertical bars */}
      <line x1="18" y1="11" x2="15" y2="37" stroke={C} strokeWidth="3" strokeLinecap="round"/>
      <line x1="30" y1="11" x2="27" y2="37" stroke={C} strokeWidth="3" strokeLinecap="round"/>
      {/* Horizontal bars */}
      <line x1="11" y1="20" x2="37" y2="20" stroke={G} strokeWidth="3" strokeLinecap="round"/>
      <line x1="10" y1="29" x2="36" y2="29" stroke={G} strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );
}

// ─── 6. FILE CONVERTER (documents + bidirectional arrow) ─────────────────────
export function FileConverterIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={26} glowY={30}/>
      {/* Back document */}
      <rect x="18" y="9" width="18" height="23" rx="3" fill={G} opacity="0.60"/>
      {/* Front document */}
      <rect x="11" y="14" width="18" height="25" rx="3" fill={C}/>
      {/* Folded corner on front doc */}
      <path d="M24 14 L24 21 L29 21" fill="none" stroke="rgba(255,255,255,0.20)" strokeWidth="1"/>
      {/* Lines inside doc */}
      <line x1="14" y1="25" x2="25" y2="25" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="14" y1="30" x2="25" y2="30" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="14" y1="35" x2="21" y2="35" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Bidirectional arrow bottom-right */}
      <circle cx="36" cy="38" r="7" fill={CARD} stroke={G} strokeWidth="1.5"/>
      <path d="M33 38 L36 35 L39 38 M36 35 L36 41" stroke={G} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── 7. TIME & DATE (clock with hands) ───────────────────────────────────────
export function TimeIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={24} glowY={26}/>
      {/* Clock face */}
      <circle cx="24" cy="26" r="15" fill="rgba(240,90,65,0.10)" stroke={C} strokeWidth="2.2"/>
      {/* Clock face inner highlight */}
      <path d="M13 18 Q24 14 35 18" stroke="rgba(255,255,255,0.10)" strokeWidth="1" fill="none"/>
      {/* Hour hand */}
      <line x1="24" y1="26" x2="24" y2="15" stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Minute hand */}
      <line x1="24" y1="26" x2="33" y2="26" stroke={C} strokeWidth="2.5" strokeLinecap="round"/>
      {/* Center dot */}
      <circle cx="24" cy="26" r="2.5" fill={C}/>
      {/* Tick marks */}
      <circle cx="24" cy="13" r="1.2" fill={G} opacity="0.7"/>
      <circle cx="37" cy="26" r="1.2" fill={G} opacity="0.5"/>
      <circle cx="24" cy="39" r="1.2" fill={G} opacity="0.5"/>
      <circle cx="11" cy="26" r="1.2" fill={G} opacity="0.5"/>
    </svg>
  );
}

// ─── 8. TEXT UTILITIES (lines + cursor) ──────────────────────────────────────
export function TextIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={22} glowY={28}/>
      {/* T crossbar + stem */}
      <line x1="10" y1="14" x2="38" y2="14" stroke={C} strokeWidth="3" strokeLinecap="round"/>
      <line x1="24" y1="14" x2="24" y2="22" stroke={C} strokeWidth="3" strokeLinecap="round"/>
      {/* Text lines */}
      <line x1="10" y1="26" x2="36" y2="26" stroke={G} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="10" y1="32" x2="30" y2="32" stroke={G} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="10" y1="38" x2="34" y2="38" stroke={G} strokeWidth="2.2" strokeLinecap="round" opacity="0.6"/>
      {/* Blinking cursor */}
      <rect x="31" y="24" width="2.5" height="10" rx="1.25" fill={C} opacity="0.90"/>
    </svg>
  );
}

// ─── 9. COMPRESSION TOOLKIT (archive layers) ─────────────────────────────────
export function CompressionIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Panel glowX={24} glowY={30}/>
      {/* Top wide layer */}
      <rect x="8"  y="10" width="32" height="8" rx="3.5" fill={G} opacity="0.75"/>
      {/* Middle layer — slightly narrower */}
      <rect x="11" y="21" width="26" height="7" rx="3" fill={C} opacity="0.85"/>
      {/* Bottom layer — narrowest */}
      <rect x="15" y="31" width="18" height="7" rx="3" fill={C}/>
      {/* Compress arrows */}
      <path d="M24 18 L24 22 M21 20.5 L24 23.5 L27 20.5" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M24 28 L24 32 M21 30.5 L24 33.5 L27 30.5" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── NAV ICONS (sidebar bottom) ──────────────────────────────────────────────
export function HomeNavIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 12 L12 3 L21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 10 L5 20 Q5 21 6 21 L10 21 L10 16 Q10 15 11 15 L13 15 Q14 15 14 16 L14 21 L18 21 Q19 21 19 20 L19 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function ExportsNavIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
      <polyline points="12,7 12,12 15,15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16.5 19.5 L19 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <line x1="17" y1="17" x2="22" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <line x1="19" y1="15" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

export function PrivacyNavIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2 L20 5.5 L20 12 Q20 17.5 12 22 Q4 17.5 4 12 L4 5.5 Z"
        stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M9 12 L11 14 L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function FaqNavIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 7 Q3 4 6 4 L18 4 Q21 4 21 7 L21 14 Q21 17 18 17 L13 17 L8 21 L8 17 L6 17 Q3 17 3 14 Z"
        stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="12" cy="13" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 9 Q10 7.5 12 7.5 Q14 7.5 14 9.5 Q14 11 12 11 L12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

export function PortfolioNavIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 13 L18 19 Q18 20 17 20 L5 20 Q4 20 4 19 L4 7 Q4 6 5 6 L11 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="15,4 20,4 20,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="10" y1="14" x2="20" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

// ─── FEATURE STRIP ICONS ──────────────────────────────────────────────────────
export function FeaturePrivacyIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2 L23 5.5 L23 13 Q23 20 14 25 Q5 20 5 13 L5 5.5 Z" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M10 13.5 L12.5 16 L18 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function FeatureInstantIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.5 3 L7 15.5 L13.5 15.5 L11.5 25 L21 12.5 L14.5 12.5 Z" fill="rgba(255,255,255,0.9)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5"/>
    </svg>
  );
}

export function FeatureNoSignupIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="10" r="5" stroke="rgba(255,255,255,0.85)" strokeWidth="1.8" fill="none"/>
      <path d="M6 24 Q6 18 14 18 Q22 18 22 24" stroke="rgba(255,255,255,0.85)" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
      <line x1="19" y1="5" x2="25" y2="11" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="25" y1="5" x2="19" y2="11" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
    </svg>
  );
}

export function FeatureOfflineIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="14" r="11" stroke="rgba(255,255,255,0.85)" strokeWidth="1.8" fill="none"/>
      <path d="M14 3 Q18 14 14 25" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" fill="none"/>
      <path d="M14 3 Q10 14 14 25" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" fill="none"/>
      <line x1="3" y1="14" x2="25" y2="14" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2"/>
      <circle cx="14" cy="14" r="3" fill="rgba(255,255,255,0.90)"/>
    </svg>
  );
}

// ─── MAIN RESOLVER ───────────────────────────────────────────────────────────
const TOOL_ICON_MAP = {
  "json-toolkit":   JsonIcon,
  "encoding":       EncodeIcon,
  "generators":     GeneratorsIcon,
  "fake-data":      FakeDataIcon,
  "hash-tools":     HashIcon,
  "file-converter": FileConverterIcon,
  "time-tools":     TimeIcon,
  "text-tools":     TextIcon,
  "compression":    CompressionIcon,
};

export function ToolIcon({ id, size = 40 }) {
  const Icon = TOOL_ICON_MAP[id];
  return Icon ? <Icon size={size} /> : null;
}
